import {
  DeesElement,
  customElement,
  html,
  css,
  cssManager,
  property,
  type TemplateResult,
} from '@design.estate/dees-element';
import { dashboardCardStyles } from './sz-dashboard-card.styles.js';

declare global {
  interface HTMLElementTagNameMap {
    'sz-traffic-card': SzTrafficCard;
  }
}

export interface ITrafficData {
  requests: number;
  errors: number;
  errorPercent: number;
  avgResponse: number;
  reqPerMin: number;
  status2xx: number;
  status3xx: number;
  status4xx: number;
  status5xx: number;
}

@customElement('sz-traffic-card')
export class SzTrafficCard extends DeesElement {
  public static demo = () => html`
    <div style="padding: 24px; max-width: 500px;">
      <sz-traffic-card
        .data=${{
          requests: 1250,
          errors: 15,
          errorPercent: 1.2,
          avgResponse: 145,
          reqPerMin: 21,
          status2xx: 1062,
          status3xx: 63,
          status4xx: 100,
          status5xx: 25,
        }}
      ></sz-traffic-card>
    </div>
  `;

  public static demoGroups = ['Dashboard'];

  @property({ type: Object })
  public accessor data: ITrafficData = {
    requests: 0,
    errors: 0,
    errorPercent: 0,
    avgResponse: 0,
    reqPerMin: 0,
    status2xx: 0,
    status3xx: 0,
    status4xx: 0,
    status5xx: 0,
  };

  public static styles = [
    cssManager.defaultStyles,
    dashboardCardStyles,
    css`
      .metrics {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: var(--dees-spacing-lg);
      }

      .metric {
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 2px;
      }

      .metric-label {
        color: var(--dees-color-text-muted);
        font-size: var(--dees-font-caption1-size);
        line-height: var(--dees-font-caption1-line);
      }

      .metric-value {
        overflow: hidden;
        color: var(--dees-color-text-primary);
        font-size: var(--dees-font-title3-size);
        font-weight: var(--dees-font-title3-weight);
        line-height: var(--dees-font-title3-line);
        letter-spacing: -0.02em;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .distribution {
        margin-top: var(--dees-spacing-lg);
        padding-top: var(--dees-spacing-lg);
        border-top: 1px solid var(--dees-color-border-subtle);
      }

      .status-bar {
        height: 8px;
        border-radius: var(--dees-radius-full);
        overflow: hidden;
        background: var(--dees-color-fill-secondary);
        display: flex;
      }

      .status-segment {
        height: 100%;
        transition: width var(--dees-transition-default) var(--dees-ease-standard);
      }

      .status-2xx,
      .legend-dot.status-2xx {
        background: var(--dees-color-accent-success);
      }

      .status-3xx,
      .legend-dot.status-3xx {
        background: var(--dees-color-accent-primary);
      }

      .status-4xx,
      .legend-dot.status-4xx {
        background: var(--dees-color-accent-warning);
      }

      .status-5xx,
      .legend-dot.status-5xx {
        background: var(--dees-color-accent-error);
      }

      .status-legend {
        margin-top: var(--dees-spacing-sm);
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: var(--dees-spacing-sm);
      }

      .legend-item {
        min-width: 0;
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-xs);
        color: var(--dees-color-text-muted);
        font-size: var(--dees-font-caption2-size);
        line-height: var(--dees-font-caption1-line);
      }

      .legend-dot {
        width: 7px;
        height: 7px;
        flex: 0 0 auto;
        border-radius: var(--dees-radius-full);
      }

      .legend-value {
        overflow: hidden;
        color: var(--dees-color-text-secondary);
        font-family: var(--dees-font-family-mono);
        text-overflow: ellipsis;
      }

      @media (max-width: 460px) {
        .status-legend {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }
      }
    `,
  ];

  public render(): TemplateResult {
    const total = this.data.status2xx + this.data.status3xx
      + this.data.status4xx + this.data.status5xx;
    const percentages = [
      total > 0 ? (this.data.status2xx / total) * 100 : 0,
      total > 0 ? (this.data.status3xx / total) * 100 : 0,
      total > 0 ? (this.data.status4xx / total) * 100 : 0,
      total > 0 ? (this.data.status5xx / total) * 100 : 0,
    ];

    return html`
      <dees-tile>
        <div slot="header" class="card-header">
          <div class="header-copy">
            <div class="card-title">Traffic</div>
            <div class="card-subtitle">Last hour · access-log metrics</div>
          </div>
          <dees-icon icon="lucide:Gauge" .iconSize=${18}></dees-icon>
        </div>

        <div class="card-content">
          <div class="metrics">
            <div class="metric">
              <span class="metric-label">Requests</span>
              <span class="metric-value">${this.formatNumber(this.data.requests)}</span>
            </div>
            <div class="metric">
              <span class="metric-label">Errors</span>
              <span class="metric-value">
                ${this.formatNumber(this.data.errors)} · ${this.data.errorPercent.toFixed(1)}%
              </span>
            </div>
            <div class="metric">
              <span class="metric-label">Average response</span>
              <span class="metric-value">${this.data.avgResponse} ms</span>
            </div>
            <div class="metric">
              <span class="metric-label">Requests per minute</span>
              <span class="metric-value">${this.formatNumber(this.data.reqPerMin)}</span>
            </div>
          </div>

          <div class="distribution">
            <div
              class="status-bar"
              role="img"
              aria-label=${`HTTP response distribution: ${this.data.status2xx} 2xx, ${this.data.status3xx} 3xx, ${this.data.status4xx} 4xx, ${this.data.status5xx} 5xx`}
            >
              ${percentages.map(
                (percentageArg, indexArg) => html`
                  <div
                    class="status-segment status-${indexArg + 2}xx"
                    style="width: ${percentageArg}%"
                  ></div>
                `
              )}
            </div>
            <div class="status-legend">
              ${[
                ['2xx', this.data.status2xx],
                ['3xx', this.data.status3xx],
                ['4xx', this.data.status4xx],
                ['5xx', this.data.status5xx],
              ].map(
                ([labelArg, valueArg], indexArg) => html`
                  <span class="legend-item">
                    <span class="legend-dot status-${indexArg + 2}xx"></span>
                    <span>${labelArg}</span>
                    <span class="legend-value">${this.formatNumber(valueArg as number)}</span>
                  </span>
                `
              )}
            </div>
          </div>
        </div>
      </dees-tile>
    `;
  }

  private formatNumber(num: number): string {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  }
}
